<template>
  <div class="page dashboard">
    <!-- HERO / COMMAND CENTER -->
    <div class="hero" :style="heroStyle">
      <div class="hero__overlay"></div>

      <div class="hero__content">
        <div class="hero__left">
          <div class="hero__kicker">Sales Command Center</div>
          <h2 class="hero__title">CRM Dashboard</h2>
          <p class="hero__sub">Today’s work.</p>

          <div class="hero__summary">
            <div class="hero__pill">
              <span class="dot dot--red"></span>
              <span class="label">Overdue</span>
              <span class="value">{{ overdueAll.length }}</span>
            </div>
            <div class="hero__pill">
              <span class="dot dot--amber"></span>
              <span class="label">Due today</span>
              <span class="value">{{ dueToday.length }}</span>
            </div>
            <div class="hero__pill">
              <span class="dot dot--orange"></span>
              <span class="label">Docs missing</span>
              <span class="value">{{ docsMissing.length }}</span>
            </div>
            <div class="hero__pill">
              <span class="dot dot--blue"></span>
              <span class="label">Ready to quote</span>
              <span class="value">{{ readyForQuotation.length }}</span>
            </div>
          </div>

          <div class="hero__actions">
            <button class="btn secondary" @click="loadAll" :disabled="loading">
              {{ loading ? 'Refreshing…' : 'Refresh' }}
            </button>
            <div class="hero__hint">All queries in one place</div>
          </div>
        </div>

        <div class="hero__right">
          <!-- Spotlight (Top 3) -->
          <div class="spotlight card card--glass">
            <div class="row row--header">
              <h3 style="margin:0;">Today’s spotlight</h3>
              <span class="badge badge--amber">{{ spotlightItems.length }}</span>
            </div>

            <div v-if="spotlightItems.length">
              <div v-for="x in spotlightItems" :key="x.key" class="spotlight__item">
                <div class="spotlight__meta">
                  <div class="spotlight__title">
                    <router-link class="item__title" :to="toLead(x.leadId)">
                      {{ x.title }}
                      <span class="muted">(#{{ x.code }})</span>
                    </router-link>
                  </div>
                  <div class="muted">
                    <b :style="{ color: x.sevColor }">{{ x.sub }}</b>
                    <span v-if="leadPhone(x.leadId)"> • {{ leadPhone(x.leadId) }}</span>
                  </div>
                </div>

                <div class="spotlight__cta">
                  <button
                    v-if="x.actionFn"
                    class="btn small"
                    @click="x.actionFn()"
                  >
                    {{ x.actionText }}
                  </button>

                  <router-link
                    v-else
                    class="btn small"
                    :to="x.actionRoute"
                  >
                    {{ x.actionText }}
                  </router-link>
                </div>
              </div>
            </div>
            <div v-else class="empty">Nothing urgent.</div>
          </div>
        </div>
      </div>
    </div>

    <!-- PRIORITY CHIPS -->
    <div class="chipbar">
      <button class="chip" :class="chipClass(overdueAll.length, 'red')" @click="scrollToCard('cardOverdue')" type="button">
        Overdue <span class="chip__count">{{ overdueAll.length }}</span>
      </button>

      <button class="chip" :class="chipClass(dueToday.length, 'amber')" @click="scrollToCard('cardDueToday')" type="button">
        Due today <span class="chip__count">{{ dueToday.length }}</span>
      </button>

      <button class="chip" :class="chipClass(docsMissing.length, 'orange')" @click="scrollToCard('cardDocsMissing')" type="button">
        Docs missing <span class="chip__count">{{ docsMissing.length }}</span>
      </button>

      <button class="chip" :class="chipClass(readyForQuotation.length, 'blue')" @click="scrollToCard('cardReady')" type="button">
        Ready for quotation <span class="chip__count">{{ readyForQuotation.length }}</span>
      </button>

      <button class="chip" :class="chipClass(quotesToSend.length, 'amber')" @click="scrollToCard('kanbanQuotes')" type="button">
        Quotations <span class="chip__count">{{ quotesToSend.length + waitingClient.length + rejectedNeedRevision.length }}</span>
      </button>

      <button class="chip" :class="chipClass(waitingClient.length, 'blue')" @click="scrollToCard('kanbanQuotes')" type="button">
        Waiting on client <span class="chip__count">{{ waitingClient.length }}</span>
      </button>

      <button class="chip" :class="chipClass(rejectedNeedRevision.length, 'red')" @click="scrollToCard('kanbanQuotes')" type="button">
        Rejected revision <span class="chip__count">{{ rejectedNeedRevision.length }}</span>
      </button>
    </div>

    <!-- PIPELINE STRIP -->
    <div class="pipeline card">
      <div class="pipeline__header">
        <div>
          <div class="pipeline__title">Pipeline</div>
          <!-- <div class="pipeline__sub">Where work gets stuck (and where people claim “it’s almost done”).</div> -->
        </div>
        <div class="pipeline__meta">
          <span class="muted">Updated:</span>
          <b>{{ fmtDT(new Date()) }}</b>
        </div>
      </div>

      <div class="pipeline__stages">
        <div class="stage">
          <div class="stage__top">
            <span class="stage__name">Leads</span>
            <span class="stage__count">{{ stageCounts.leads }}</span>
          </div>
          <div class="stage__bar">
            <div class="stage__fill" :style="{ width: pct(stageCounts.leads) }"></div>
          </div>
        </div>

        <div class="stage">
          <div class="stage__top">
            <span class="stage__name">Inquiries</span>
            <span class="stage__count">{{ stageCounts.inquiries }}</span>
          </div>
          <div class="stage__bar">
            <div class="stage__fill" :style="{ width: pct(stageCounts.inquiries) }"></div>
          </div>
        </div>

        <div class="stage">
          <div class="stage__top">
            <span class="stage__name">Docs pending</span>
            <span class="stage__count">{{ stageCounts.docsPending }}</span>
          </div>
          <div class="stage__bar stage__bar--warn">
            <div class="stage__fill" :style="{ width: pct(stageCounts.docsPending) }"></div>
          </div>
        </div>

        <div class="stage">
          <div class="stage__top">
            <span class="stage__name">Ready to quote</span>
            <span class="stage__count">{{ stageCounts.readyToQuote }}</span>
          </div>
          <div class="stage__bar stage__bar--blue">
            <div class="stage__fill" :style="{ width: pct(stageCounts.readyToQuote) }"></div>
          </div>
        </div>

        <div class="stage">
          <div class="stage__top">
            <span class="stage__name">Drafts</span>
            <span class="stage__count">{{ stageCounts.drafts }}</span>
          </div>
          <div class="stage__bar stage__bar--amber">
            <div class="stage__fill" :style="{ width: pct(stageCounts.drafts) }"></div>
          </div>
        </div>

        <div class="stage">
          <div class="stage__top">
            <span class="stage__name">Waiting on client</span>
            <span class="stage__count">{{ stageCounts.waitingClient }}</span>
          </div>
          <div class="stage__bar stage__bar--blue">
            <div class="stage__fill" :style="{ width: pct(stageCounts.waitingClient) }"></div>
          </div>
        </div>

        <div class="stage">
          <div class="stage__top">
            <span class="stage__name">Rejected</span>
            <span class="stage__count">{{ stageCounts.rejected }}</span>
          </div>
          <div class="stage__bar stage__bar--red">
            <div class="stage__fill" :style="{ width: pct(stageCounts.rejected) }"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- =============================== -->
    <!-- MASONRY (Cap height + Top N)    -->
    <!-- =============================== -->
    <div class="masonry" style="margin-top:14px;">
      <!-- OVERDUE QUEUE -->
      <div class="card card--masonry" :class="{ 'card--flash': flashCard === 'cardOverdue' }" ref="cardOverdue" data-card="cardOverdue">
        <div class="row row--header">
          <div class="row" style="gap:10px;align-items:center;">
            <h3 style="margin:0;">Overdue</h3>
            <span class="badge" :class="overdueAll.length ? 'badge--red' : 'badge--green'">{{ overdueAll.length }}</span>
          </div>

          <div class="row" style="gap:8px;align-items:center;">
            <div class="seg">
              <button class="seg__btn" :class="{active: overdueTab==='all'}" @click="overdueTab='all'">All</button>
              <button class="seg__btn" :class="{active: overdueTab==='followups'}" @click="overdueTab='followups'">Follow-ups</button>
              <button class="seg__btn" :class="{active: overdueTab==='docs'}" @click="overdueTab='docs'">Docs</button>
              <button class="seg__btn" :class="{active: overdueTab==='quotes'}" @click="overdueTab='quotes'">Quotations</button>
            </div>

            <button class="btn tiny secondary" @click="toggleOverdueSort">
              Sort: {{ overdueSortLabel }}
            </button>
          </div>
        </div>

        <div class="card__body card__body--cap">
          <div v-if="filteredOverduePreview.length">
            <div v-for="x in filteredOverduePreview" :key="x.key" class="item item--queue">
              <div class="item__main">
                <router-link class="item__title" :to="toLead(x.leadId)">
                  {{ x.title }}
                  <span class="muted">(#{{ x.code }})</span>
                </router-link>

                <div class="muted">
                  <b :style="{color: x.sevColor}">{{ x.sub }}</b>
                  <span v-if="leadPhone(x.leadId)"> • {{ leadPhone(x.leadId) }}</span>
                  <span v-if="leadStatus(x.leadId)==='converted_to_inquiry'"> • <b>Inquiry</b></span>
                </div>

                <div class="helper" v-if="x.note">{{ x.note }}</div>
              </div>

              <div class="item__side">
                <button v-if="x.actionFn" class="btn small" @click="x.actionFn()">
                  {{ x.actionText }}
                </button>

                <router-link v-else class="btn small" :to="x.actionRoute">
                  {{ x.actionText }}
                </router-link>
              </div>
            </div>
          </div>

          <div v-else class="empty">Nothing overdue yet.</div>
        </div>

        <div class="card__footer" v-if="filteredOverdueAll.length > limits.overdue">
          <button class="footerLink" type="button" @click="goToOverdueFull()">
            View all {{ filteredOverdueAll.length }}
          </button>
        </div>
      </div>

      <!-- DUE TODAY -->
      <div class="card card--masonry" :class="{ 'card--flash': flashCard === 'cardDueToday' }" ref="cardDueToday" data-card="cardDueToday">
        <div class="row row--header">
          <h3 style="margin:0;">Follow-ups due today</h3>
          <span class="badge" :class="dueToday.length ? 'badge--amber' : 'badge--green'">{{ dueToday.length }}</span>
        </div>

        <div class="card__body card__body--cap">
          <div v-if="dueTodayPreview.length">
            <div v-for="x in dueTodayPreview" :key="x.id" class="item">
              <div class="item__main">
                <router-link class="item__title" :to="toLead(x.leadId)">
                  {{ leadName(x.leadId) }}
                  <span class="muted">(#{{ leadCode(x.leadId) }})</span>
                </router-link>
                <div class="muted">
                  Due: <b>{{ prettyDate(x.dueDate) }}</b>
                  <span v-if="leadPhone(x.leadId)"> • {{ leadPhone(x.leadId) }}</span>
                  <span v-if="leadStatus(x.leadId)==='converted_to_inquiry'"> • <b>Inquiry</b></span>
                </div>
                <div class="helper">{{ x.note }}</div>
              </div>
              <div class="item__side">
                <button class="btn small" @click="markDone(x)">Mark done</button>
              </div>
            </div>
          </div>
          <div v-else class="empty">Nothing due today.</div>
        </div>

        <div class="card__footer" v-if="dueToday.length > limits.dueToday">
          <router-link class="footerLink" :to="{ name:'sales-followups' }">
            View all {{ dueToday.length }}
          </router-link>
        </div>
      </div>

      <!-- INQUIRY DOC SLA -->
      <div class="card card--masonry" :class="{ 'card--flash': flashCard === 'cardDocsMissing' }" ref="cardDocsMissing" data-card="cardDocsMissing">
        <div class="row row--header">
          <h3 style="margin:0;">Inquiries missing docs</h3>
          <span class="badge" :class="docsMissing.length ? 'badge--red' : 'badge--green'">{{ docsMissing.length }}</span>
        </div>

        <div class="card__body card__body--cap">
          <div v-if="docsMissingPreview.length">
            <div v-for="l in docsMissingPreview" :key="l.id" class="item">
              <div class="item__main">
                <router-link class="item__title" :to="toLead(l.id)">
                  {{ l.name }} <span class="muted">(#{{ l.leadCode || l.id }})</span>
                </router-link>

                <div class="muted">
                  Converted: <b>{{ fmtDT(l.convertedAt) }}</b>
                  • Deadline:
                  <b :style="{color: docsDeadlineColor(l)}">{{ prettyDate(deadline(l)) }}</b>
                </div>

                <div class="helper">
                  Photos: <b>{{ docsMap[String(l.id)] ? docsMap[String(l.id)].sitePhotos : 0 }}</b> •
                  Measurements: <b>{{ docsMap[String(l.id)] ? docsMap[String(l.id)].measurements : 0 }}</b>
                </div>
              </div>

              <div class="item__side">
                <router-link class="btn small" :to="toLead(l.id)">Upload docs</router-link>
              </div>
            </div>
          </div>
          <div v-else class="empty">All inquiries have required docs. Shocking.</div>
        </div>

        <div class="card__footer" v-if="docsMissing.length > limits.docsMissing">
          <router-link class="footerLink" :to="{ name:'sales-customers' }">
            View all {{ docsMissing.length }}
          </router-link>
        </div>
      </div>

      <!-- READY FOR QUOTATION -->
      <div class="card card--masonry" :class="{ 'card--flash': flashCard === 'cardReady' }" ref="cardReady" data-card="cardReady">
        <div class="row row--header">
          <h3 style="margin:0;">Ready for quotation</h3>
          <span class="badge" :class="readyForQuotation.length ? 'badge--amber' : 'badge--green'">
            {{ readyForQuotation.length }}
          </span>
        </div>

        <div class="card__body card__body--cap">
          <div v-if="readyForQuotationPreview.length">
            <div v-for="x in readyForQuotationPreview" :key="x.id" class="item">
              <div class="item__main">
                <router-link class="item__title" :to="toLead(x.id)">
                  {{ x.name }} <span class="muted">(#{{ x.leadCode || x.id }})</span>
                </router-link>

                <div class="muted">
                  Docs: <b style="color:#10b981">OK</b>
                  <span v-if="x.phone"> • {{ x.phone }}</span>
                  <span v-if="x.convertedAt"> • Converted: <b>{{ fmtDT(x.convertedAt) }}</b></span>
                </div>

                <div class="helper">Quotation not started yet.</div>
              </div>

              <div class="item__side">
                <button class="btn small" @click="createDraftForInquiry(x.id)">Create draft</button>
              </div>
            </div>
          </div>

          <div v-else class="empty">Nothing ready right now.</div>
        </div>

        <div class="card__footer" v-if="readyForQuotation.length > limits.ready">
          <router-link class="footerLink" :to="{ name:'sales-customers' }">
            View all {{ readyForQuotation.length }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- ===================================== -->
    <!-- PREMIUM KANBAN STRIP (Quotations)     -->
    <!-- ===================================== -->
    <div
      class="kanban"
      ref="kanbanQuotes"
      data-card="kanbanQuotes"
      :class="{ 'card--flash': flashCard === 'kanbanQuotes' }"
    >
      <div class="kanban__top">
        <div>
          <div class="kanban__title">Quotation Desk</div>
          <div class="kanban__sub">Three lanes. Zero confusion.</div>
        </div>
        <div class="kanban__actions">
          <router-link class="btn tiny secondary" :to="{ name:'sales-quotations' }">Open Quotations</router-link>
          <button class="btn tiny secondary" type="button" @click="scrollToCard('cardOverdue')">Back to top</button>
        </div>
      </div>

      <div class="kanban__strip">
        <!-- Lane: To send -->
        <div class="lane">
          <div class="lane__head">
            <div class="lane__label">
              <span class="lane__dot lane__dot--amber"></span>
              To send
            </div>
            <span class="badge" :class="quotesToSend.length ? 'badge--amber' : 'badge--green'">{{ quotesToSend.length }}</span>
          </div>

          <div class="lane__body">
            <div v-if="quotesToSendPreview.length">
              <div v-for="q in quotesToSendPreview" :key="q.id" class="laneCard">
                <div class="laneCard__main">
                  <router-link class="laneCard__title" :to="toQuote(q.inquiryId, q.leadId)">
                    {{ leadName(q.leadId) }}
                    <span class="muted">• v{{ q.version }} (#{{ shortId(q.id) }})</span>
                  </router-link>
                  <div class="muted">
                    Docs: <b style="color:#10b981">OK</b>
                    <span v-if="leadPhone(q.leadId)"> • {{ leadPhone(q.leadId) }}</span>
                  </div>
                  <div class="helper">Draft ready. Send it, then mark Sent.</div>
                </div>
                <div class="laneCard__cta">
                  <router-link class="btn small" :to="toQuote(q.inquiryId, q.leadId)">Open</router-link>
                </div>
              </div>
            </div>
            <div v-else class="empty empty--tight">No drafts pending send.</div>
          </div>

          <div class="lane__foot" v-if="quotesToSend.length > limits.quoteLane">
            <router-link class="footerLink" :to="{ name:'sales-quotations' }">
              View all {{ quotesToSend.length }}
            </router-link>
          </div>
        </div>

        <!-- Lane: Waiting -->
        <div class="lane">
          <div class="lane__head">
            <div class="lane__label">
              <span class="lane__dot lane__dot--blue"></span>
              Waiting on client
            </div>
            <span class="badge" :class="waitingClient.length ? 'badge--amber' : 'badge--green'">{{ waitingClient.length }}</span>
          </div>

          <div class="lane__body">
            <div v-if="waitingClientPreview.length">
              <div v-for="q in waitingClientPreview" :key="q.id" class="laneCard">
                <div class="laneCard__main">
                  <router-link class="laneCard__title" :to="toQuote(q.inquiryId, q.leadId)">
                    {{ leadName(q.leadId) }}
                    <span class="muted">• v{{ q.version }} (#{{ shortId(q.id) }})</span>
                  </router-link>
                  <div class="muted">
                    Status: <b style="color:#2563eb">{{ q.status || 'Sent' }}</b>
                    <span v-if="leadPhone(q.leadId)"> • {{ leadPhone(q.leadId) }}</span>
                  </div>
                  <div class="helper">Awaiting approval or signature.</div>
                </div>
                <div class="laneCard__cta">
                  <router-link class="btn small" :to="toQuote(q.inquiryId, q.leadId)">Open</router-link>
                </div>
              </div>
            </div>
            <div v-else class="empty empty--tight">No quotes pending client response.</div>
          </div>

          <div class="lane__foot" v-if="waitingClient.length > limits.quoteLane">
            <router-link class="footerLink" :to="{ name:'sales-quotations' }">
              View all {{ waitingClient.length }}
            </router-link>
          </div>
        </div>

        <!-- Lane: Rejected -->
        <div class="lane">
          <div class="lane__head">
            <div class="lane__label">
              <span class="lane__dot lane__dot--red"></span>
              Rejected (revise)
            </div>
            <span class="badge" :class="rejectedNeedRevision.length ? 'badge--red' : 'badge--green'">{{ rejectedNeedRevision.length }}</span>
          </div>

          <div class="lane__body">
            <div v-if="rejectedNeedRevisionPreview.length">
              <div v-for="q in rejectedNeedRevisionPreview" :key="q.id" class="laneCard laneCard--danger">
                <div class="laneCard__main">
                  <router-link class="laneCard__title" :to="toQuote(q.inquiryId, q.leadId)">
                    {{ leadName(q.leadId) }}
                    <span class="muted">• v{{ q.version }} (#{{ shortId(q.id) }})</span>
                  </router-link>
                  <div class="muted">
                    Rejected: <b style="color:#dc2626">{{ q.rejectedReason || '—' }}</b>
                    <span v-if="q.rejectedAt"> • {{ fmtDT(q.rejectedAt) }}</span>
                  </div>
                  <div class="helper">Create Revision (v+1) with mandatory change note.</div>
                </div>
                <div class="laneCard__cta">
                  <router-link class="btn small" :to="toQuote(q.inquiryId, q.leadId)">Open</router-link>
                </div>
              </div>
            </div>
            <div v-else class="empty empty--tight">No rejected quotes waiting revision.</div>
          </div>

          <div class="lane__foot" v-if="rejectedNeedRevision.length > limits.quoteLane">
            <router-link class="footerLink" :to="{ name:'sales-quotations' }">
              View all {{ rejectedNeedRevision.length }}
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- FLOATING QUICK ACTIONS -->
    <div class="dock" aria-label="Quick actions">
      <router-link class="dock__btn" :to="{ name:'sales-customers' }" title="Clients">
        Clients
      </router-link>
      <router-link class="dock__btn" :to="{ name:'sales-followups' }" title="Follow-ups">
        Follow-ups
      </router-link>
      <router-link class="dock__btn dock__btn--primary" :to="{ name:'sales-quotations' }" title="Quotations">
        Quotations
      </router-link>
      <button class="dock__btn" @click="loadAll" :disabled="loading" title="Refresh">
        {{ loading ? '…' : 'Refresh' }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SalesDashboard',
  data(){
    return {
      loading: false,

      // Single endpoint result (raw)
      dash: null,

      // normalized for existing UI helpers
      leads: [],
      inquiriesRaw: [],
      followups: [],
      quotes: [],
      docsMap: {},
      readyForQuotation: [],

      flash: '',
      flashCard: '',
      flashTimer: null,

      // UI-only
      heroIndex: 0,
      heroTimer: null,
      overdueTab: 'all', // all | followups | docs | quotes
      overdueSort: 'most_overdue_first', // most_overdue_first | newest_first

      // Masonry + cap + top N limits
      limits: {
        overdue: 8,
        dueToday: 8,
        docsMissing: 8,
        ready: 8,
        quoteLane: 6
      },

      // matches backend
      DASHBOARD_ENDPOINT: '/dashboard/sales',

      heroImages: [
        { key:'k1', src: null },
        { key:'k2', src: null },
        { key:'v1', src: null },
        { key:'v2', src: null },
        { key:'w1', src: null }
      ]
    }
  },

  computed:{
    heroStyle(){
      var img = this.getHeroImage()
      var url = img ? img : ''
      return { backgroundImage: url ? ("url(" + url + ")") : 'none' }
    },

    overdueSortLabel(){
      return this.overdueSort === 'most_overdue_first' ? 'Most overdue' : 'Newest'
    },

    todayISO(){
      const d = new Date()
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },

    // --- Inquiries (vision-aligned) ---
    inquiries(){
      if (Array.isArray(this.inquiriesRaw) && this.inquiriesRaw.length){
        const out = []
        for (let i=0;i<this.inquiriesRaw.length;i++){
          const q = this.inquiriesRaw[i] || {}
          const leadId = q.leadId != null ? q.leadId : (q.lead_id != null ? q.lead_id : q.lead)
          const lead = this.leadById(leadId)
          if (!lead) continue

          out.push({
            id: lead.id, // keep id = leadId for routes/templates
            inquiryId: q.id != null ? q.id : q.inquiryId,
            inquiryStatus: (q.status || q.inquiryStatus || '').toUpperCase(),
            createdAt: q.createdAt || q.created_at || lead.createdAt || lead.created_at,
            convertedAt: lead.convertedAt || lead.converted_at || lead.createdAt || lead.created_at,
            docsDueAt: q.docsDueAt || q.docs_due_at || null,
            name: lead.name,
            phone: lead.phone,
            leadCode: lead.leadCode || lead.lead_code || lead.id
          })
        }
        return out
      }

      return (this.leads || []).filter(l => String(l.status || '').toLowerCase() === 'converted_to_inquiry')
    },

    // Followups overdue / dueToday
    overdue(){
      const today = this.todayISO
      return (this.followups || [])
        .filter(f => Number(f.isDone) === 0)
        .filter(f => this.toYMD(f.dueDate) < today)
        .sort((a,b) => this.toYMD(a.dueDate).localeCompare(this.toYMD(b.dueDate)))
    },

    dueToday(){
      const today = this.todayISO
      return (this.followups || [])
        .filter(f => Number(f.isDone) === 0)
        .filter(f => this.toYMD(f.dueDate) === today)
        .sort((a,b) => String(a.dueDate).localeCompare(String(b.dueDate)))
    },

    dueTodayPreview(){
      return (this.dueToday || []).slice(0, this.limits.dueToday)
    },

    // Docs missing (inquiries still pending docs)
    docsMissing(){
      return (this.inquiries || [])
        .filter(l => {
          if (l.inquiryStatus) return l.inquiryStatus === 'DOCS_PENDING'
          const st = this.docsMap[String(l.id)]
          return !(st && st.docsOk)
        })
        .sort((a,b) => {
          const da = new Date(a.convertedAt || a.createdAt || 0).getTime()
          const db = new Date(b.convertedAt || b.createdAt || 0).getTime()
          return da - db
        })
    },

    docsMissingPreview(){
      return (this.docsMissing || []).slice(0, this.limits.docsMissing)
    },

    readyForQuotationPreview(){
      return (this.readyForQuotation || []).slice(0, this.limits.ready)
    },

    // Quotations lanes
    quotesToSend(){
      // mapped from dash.quoteActionNeeded.needSend (+ needQuotePdf optionally)
      return (this.quotes || [])
        .filter(q => (q.status||'').toLowerCase() === 'draft' || (q.status||'').toLowerCase() === 'revised')
        .sort((a,b) => (b.version||0) - (a.version||0))
    },

    quotesToSendPreview(){
      return (this.quotesToSend || []).slice(0, this.limits.quoteLane)
    },

    waitingClient(){
      return (this.quotes || [])
        .filter(q => (q.status||'').toLowerCase() === 'sent')
        .sort((a,b) => (b.version||0) - (a.version||0))
    },

    waitingClientPreview(){
      return (this.waitingClient || []).slice(0, this.limits.quoteLane)
    },

    rejectedNeedRevision(){
      // Your current endpoint doesn’t emit rejected lane; keep lane empty.
      return (this.quotes || [])
        .filter(q => (q.status||'').toLowerCase() === 'rejected')
        .sort((a,b) => (b.version||0) - (a.version||0))
    },

    rejectedNeedRevisionPreview(){
      return (this.rejectedNeedRevision || []).slice(0, this.limits.quoteLane)
    },

    overdueDocs(){
      const now = Date.now()
      return (this.docsMissing || [])
        .filter(l => {
          const dl = this.deadline(l)
          return dl && now > dl.getTime()
        })
    },

    overdueQuotation(){
      // Your backend already identifies “needSend / needQuotePdf” etc.
      // We keep this legacy bucket empty to avoid inventing new SLA logic.
      return []
    },

    overdueAll(){
      const items = []

      ;(this.overdue || []).forEach(f => {
        items.push({
          type: 'followup',
          key: 'f-' + f.id,
          leadId: f.leadId,
          title: this.leadName(f.leadId),
          code: this.leadCode(f.leadId),
          sub: 'Follow-up overdue: ' + this.prettyDate(f.dueDate),
          note: f.note || '',
          actionText: 'Mark done',
          actionFn: () => this.markDone(f),
          sev: this.calcOverdueSeverityFollowup(f),
          sevColor: '#dc2626'
        })
      })

      ;(this.overdueDocs || []).forEach(l => {
        const st = (this.docsMap[String(l.id)] || {})
        const dl = this.deadline(l)
        items.push({
          type: 'docs',
          key: 'd-' + l.id,
          leadId: l.id,
          title: l.name,
          code: (l.leadCode || l.id),
          sub: 'Docs overdue. Deadline: ' + this.prettyDate(dl),
          note: 'Photos: ' + (st.sitePhotos || 0) + ' • Measurements: ' + (st.measurements || 0),
          actionText: 'Upload docs',
          actionRoute: this.toLead(l.id),
          sev: this.calcOverdueSeverityTime(l.convertedAt || l.createdAt),
          sevColor: '#b45309'
        })
      })

      // Quote overdue is not calculated locally anymore (backend buckets handle the kanban)
      return this.sortOverdueItems(items, this.overdueSort)
    },

    filteredOverdueAll(){
      var list = (this.overdueAll || [])
      if (this.overdueTab === 'followups') list = list.filter(x => x.type === 'followup')
      else if (this.overdueTab === 'docs') list = list.filter(x => x.type === 'docs')
      else if (this.overdueTab === 'quotes') list = list.filter(x => x.type === 'quote')
      return list
    },

    filteredOverduePreview(){
      return (this.filteredOverdueAll || []).slice(0, this.limits.overdue)
    },

    spotlightItems(){
      const all = (this.overdueAll || [])
      if (!all.length) return []
      return all.slice(0, 3)
    },

    stageCounts(){
      // Use backend counts where possible (more accurate)
      const c = (this.dash && this.dash.counts) ? this.dash.counts : null

      const leads = c ? (c.leadsFollowupDue || 0) : (this.leads || []).filter(l => String(l.status||'').toLowerCase() === 'follow_up').length
      const inquiries = c ? ((c.inquiriesOverdue || 0) + (c.inquiriesDocsPending || 0) + (c.inquiriesReadyForQuotation || 0)) : (this.inquiries || []).length
      const docsPending = c ? ((c.inquiriesOverdue || 0) + (c.inquiriesDocsPending || 0)) : (this.docsMissing || []).length
      const readyToQuote = c ? (c.inquiriesReadyForQuotation || 0) : (this.readyForQuotation || []).length

      let drafts = 0
      let waiting = 0
      let rejected = 0

      for (let i=0;i<(this.quotes||[]).length;i++){
        const q = this.quotes[i]
        const st = String(q.status || '').toLowerCase()
        if (st === 'draft' || st === 'revised') drafts++
        if (st === 'sent') waiting++
        if (st === 'rejected') rejected++
      }

      return {
        leads: leads,
        inquiries: inquiries,
        docsPending: docsPending,
        readyToQuote: readyToQuote,
        drafts: drafts,
        waitingClient: waiting,
        rejected: rejected
      }
    },

    maxStageCount(){
      const s = this.stageCounts
      const arr = [s.leads, s.inquiries, s.docsPending, s.readyToQuote, s.drafts, s.waitingClient, s.rejected]
      let m = 0
      for (let i=0;i<arr.length;i++){
        if (arr[i] > m) m = arr[i]
      }
      return m || 1
    }
  },

  created(){
    try{
      this.heroImages[0].src = require('@/assets/kitchen1.jpg')
      this.heroImages[1].src = require('@/assets/kitchen2.jpg')
      this.heroImages[2].src = require('@/assets/vanity1.jpeg')
      this.heroImages[3].src = require('@/assets/vanity2.jpg')
      this.heroImages[4].src = require('@/assets/wardrobe1.jpg')
    } catch(e){
      console.warn('Hero images missing or paths incorrect', e)
    }

    this.loadAll()
  },

  mounted(){
    var self = this
    this.heroTimer = setInterval(function(){
      self.heroIndex = (self.heroIndex + 1) % self.heroImages.length
    }, 6000)
  },

  beforeDestroy(){
    if (this.heroTimer) clearInterval(this.heroTimer)
    this.heroTimer = null
    if (this.flashTimer){
      clearTimeout(this.flashTimer)
      this.flashTimer = null
    }
  },

  methods:{
    // ---------- UI helpers ----------
    getHeroImage(){
      const img = this.heroImages && this.heroImages.length ? this.heroImages[this.heroIndex] : null
      return img && img.src ? img.src : null
    },

    toggleOverdueSort(){
      this.overdueSort = (this.overdueSort === 'most_overdue_first') ? 'newest_first' : 'most_overdue_first'
    },

    goToOverdueFull(){
      this.$router.push({ name:'sales-followups' })
    },

    scrollToCard(cardId){
      try{
        this.$nextTick(() => {
          let node = this.$refs[cardId]
          node = Array.isArray(node) ? node[0] : node

          if (!node) node = this.$el.querySelector('[data-card="' + cardId + '"]')
          if (!node) return

          const OFFSET = 96

          const getScrollParent = (el) => {
            let p = el.parentElement
            while (p) {
              const s = window.getComputedStyle(p)
              const oy = s.overflowY
              if (oy === 'auto' || oy === 'scroll') return p
              p = p.parentElement
            }
            return document.scrollingElement || document.documentElement
          }

          const scroller = getScrollParent(node)

          const nodeRect = node.getBoundingClientRect()
          const scrollerRect = scroller === document.documentElement || scroller === document.body || scroller === document.scrollingElement
            ? { top: 0 }
            : scroller.getBoundingClientRect()

          const currentTop = (scroller === document.scrollingElement || scroller === document.documentElement)
            ? (window.pageYOffset || document.documentElement.scrollTop || 0)
            : scroller.scrollTop

          const targetTop = currentTop + (nodeRect.top - scrollerRect.top) - OFFSET

          if (scroller === document.scrollingElement || scroller === document.documentElement) {
            window.scrollTo({ top: targetTop, behavior: 'smooth' })
          } else {
            scroller.scrollTo({ top: targetTop, behavior: 'smooth' })
          }

          this.flashCard = cardId
          if (this.flashTimer) clearTimeout(this.flashTimer)

          this.flashTimer = setTimeout(() => {
            if (this.flashCard === cardId) this.flashCard = ''
            this.flashTimer = null
          }, 2400)
        })
      } catch(e){
        console.error('scrollToCard failed', e)
      }
    },

    chipClass(count, tone){
      const c = Number(count || 0)
      const base = ['chip--' + tone]
      if (c <= 0) base.push('chip--quiet')
      else if (c >= 5) base.push('chip--hot')
      else base.push('chip--mid')
      return base
    },

    pct(n){
      const val = Number(n || 0)
      const max = this.maxStageCount
      const p = Math.round((val / max) * 100)
      return String(p) + '%'
    },

    // ---------- routing ----------
    toLead(id){ return { name:'sales-customer-detail', params:{ id } } },
    // routing
    toQuote(inquiryId, leadId){
      if (!inquiryId) return this.toLead(leadId)
      return {
        name: 'sales-quotations',
        query: {
          inquiryId: String(inquiryId),
          ...(leadId ? { leadId: String(leadId) } : {})
        }
      }
    },


    // ---------- normalization ----------
    normalizeLead(x){
      x = x || {}
      return {
        id: x.id,
        name: x.name,
        phone: x.phone,
        email: x.email,
        address: x.address,
        status: x.status,
        leadCode: x.leadCode || x.lead_code || x.code || x.id,
        createdAt: x.createdAt || x.created_at || null,
        convertedAt: x.convertedAt || x.converted_at || null
      }
    },

    normalizeInquiry(i){
      i = i || {}
      return {
        id: i.id,
        leadId: i.leadId != null ? i.leadId : i.lead_id,
        status: i.status,
        createdAt: i.createdAt || i.created_at,
        docsDueAt: i.docsDueAt || i.docs_due_at
      }
    },

    normalizeFollowupFromLeadDueRow(l){
      // backend: leadsFollowupDue rows are basically a followup reminder on the lead itself
      l = l || {}
      const leadId = l.id
      const due = l.followUpDate || l.follow_up_date
      return {
        id: 'lead-' + leadId,      // no followup id; stable key
        leadId: leadId,
        dueDate: due,
        note: '',                  // not provided in endpoint
        isDone: 0
      }
    },

    normalizeQuoteActionRow(r, forcedStatus){
      r = r || {}
      const leadId = r.leadId != null ? r.leadId : r.lead_id
      return {
        id: r.quotationId || r.quotation_id,
        leadId: leadId,
        inquiryId: r.inquiryId || r.inquiry_id,
        status: forcedStatus || r.status,
        version: r.version || 1,
        orderId: null,
        rejectedReason: r.rejectedReason || r.rejected_reason,
        rejectedAt: r.rejectedAt || r.rejected_at,
        sentAt: r.sentAt || r.sent_at
      }
    },

    // ---------- lead helpers ----------
    leadById(id){
      return (this.leads || []).find(l => String(l.id)===String(id)) || null
    },
    leadName(id){
      const l = this.leadById(id); return l ? l.name : ('Lead #' + id)
    },
    leadCode(id){
      const l = this.leadById(id); return l ? (l.leadCode || l.id) : id
    },
    leadPhone(id){
      const l = this.leadById(id); return l ? (l.phone || '') : ''
    },
    leadStatus(id){
      const l = this.leadById(id); return l ? String(l.status || '').toLowerCase() : ''
    },

    inquiryIdForLead(leadId){
      const arr = (this.inquiriesRaw || [])
      for (let i=0;i<arr.length;i++){
        const it = arr[i]
        const lid = it.leadId != null ? it.leadId : it.lead_id
        if (String(lid) === String(leadId)){
          return it.id != null ? it.id : it.inquiryId
        }
      }
      return null
    },

    // ---------- dates ----------
    deadline(l){
      if (l && l.docsDueAt){
        const d = new Date(l.docsDueAt)
        return isNaN(d.getTime()) ? null : d
      }
      if (!l || !l.convertedAt) return null
      const t = new Date(l.convertedAt)
      return new Date(t.getTime() + 24*60*60*1000)
    },
    docsDeadlineColor(l){
      const dl = this.deadline(l)
      if (!dl) return '#111827'
      const now = Date.now()
      return now > dl.getTime() ? '#dc2626' : '#92400e'
    },
    fmtDT(d){
      if (!d) return '—'
      try{
        const dt = (d instanceof Date) ? d : new Date(d)
        if (isNaN(dt.getTime())) return String(d)
        return dt.toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })
      } catch(e){ return String(d) }
    },
    toYMD(v){
      if (!v) return ''
      const s = String(v)
      if (s.length >= 10 && s[4] === '-' && s[7] === '-') return s.slice(0,10)
      const d = new Date(v)
      if (isNaN(d.getTime())) return s.slice(0,10)
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2,'0')
      const dd = String(d.getDate()).padStart(2,'0')
      return `${yyyy}-${mm}-${dd}`
    },
    prettyDate(d){
      if (!d) return '—'
      let date
      if (d instanceof Date) {
        date = new Date(d.getFullYear(), d.getMonth(), d.getDate())
      } else {
        const s = String(d)
        if (s.length >= 10 && s[4] === '-' && s[7] === '-') {
          date = new Date(s.slice(0,10) + 'T00:00:00')
        } else {
          const tmp = new Date(d)
          if (isNaN(tmp.getTime())) return s
          date = new Date(tmp.getFullYear(), tmp.getMonth(), tmp.getDate())
        }
      }
      if (isNaN(date.getTime())) return String(d)

      const today = new Date()
      const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()
      const d0 = date.getTime()
      const diff = Math.round((d0 - t0) / 86400000)

      if (diff === 0) return 'Today'
      if (diff === 1) return 'Tomorrow'
      if (diff === -1) return 'Yesterday'

      return date.toLocaleDateString('en-GB', { day:'numeric', month:'long' })
    },

    // ---------- sorting/severity ----------
    calcOverdueSeverityFollowup(f){
      const ymd = this.toYMD(f && f.dueDate ? f.dueDate : '')
      if (!ymd) return 0
      const t = new Date(ymd + 'T00:00:00').getTime()
      if (isNaN(t)) return 0
      const now = Date.now()
      const days = Math.floor((now - t) / 86400000)
      return days > 0 ? days : 0
    },

    calcOverdueSeverityTime(convertedAt){
      const t = new Date(convertedAt || 0).getTime()
      if (!t || isNaN(t)) return 0
      const now = Date.now()
      const hours = Math.floor((now - t) / 3600000)
      return hours > 0 ? hours : 0
    },

    sortOverdueItems(items, mode){
      const list = (items || []).slice(0)
      if (mode === 'newest_first') {
        return list.sort((a,b) => {
          const at = this.itemTimeGuess(a)
          const bt = this.itemTimeGuess(b)
          return bt - at
        })
      }
      return list.sort((a,b) => {
        const as = Number(a.sev || 0)
        const bs = Number(b.sev || 0)
        if (bs !== as) return bs - as
        const at = this.itemTimeGuess(a)
        const bt = this.itemTimeGuess(b)
        return at - bt
      })
    },

    itemTimeGuess(x){
      if (!x) return 0
      if (x.type === 'followup') {
        const ymd = this.toYMD(x && x.dueDate ? x.dueDate : '')
        if (!ymd) return 0
        const t = new Date(ymd + 'T00:00:00').getTime()
        return isNaN(t) ? 0 : t
      }
      const l = this.leadById(x.leadId)
      const t2 = new Date((l && l.convertedAt) ? l.convertedAt : (l && l.createdAt ? l.createdAt : 0)).getTime()
      return isNaN(t2) ? 0 : t2
    },

    // ---------- API ----------
    async loadAll(){
      this.loading = true
      try{
        const { data } = await this.$api.get(this.DASHBOARD_ENDPOINT)
        this.dash = data || null

        const leadsIndex = {}
        const upsertLead = (leadId, patch) => {
          if (!leadId) return
          const k = String(leadId)
          if (!leadsIndex[k]) leadsIndex[k] = { id: leadId }
          Object.assign(leadsIndex[k], patch || {})
        }

        // A) followup due leads -> leads + followups
        const due = Array.isArray(data && data.leadsFollowupDue) ? data.leadsFollowupDue : []
        for (let i=0;i<due.length;i++){
          const l = due[i]
          upsertLead(l.id, {
            id: l.id,
            name: l.name,
            phone: l.phone,
            email: l.email,
            status: 'follow_up',
            leadCode: l.leadCode,
            createdAt: l.createdAt
          })
        }
        this.followups = due.map(this.normalizeFollowupFromLeadDueRow)

        // B) inquiries buckets -> inquiriesRaw + leads
        const inqOver = Array.isArray(data && data.inquiriesOverdue) ? data.inquiriesOverdue : []
        const inqPend = Array.isArray(data && data.inquiriesDocsPending) ? data.inquiriesDocsPending : []
        const allInq = inqOver.concat(inqPend)

        this.inquiriesRaw = allInq.map((r) => this.normalizeInquiry(r))

        for (let i=0;i<allInq.length;i++){
          const r = allInq[i]
          upsertLead(r.leadId, {
            id: r.leadId,
            name: r.customerName,
            phone: r.customerPhone,
            status: 'converted_to_inquiry',
            leadCode: r.leadCode,
            convertedAt: r.createdAt
          })
        }

        // docsMap (for UI counts display). Endpoint doesn’t return doc counts, so default 0.
        // Keyed by leadId (because your template uses l.id where l.id is leadId)
        const docsMap = {}
        for (let i=0;i<allInq.length;i++){
          const r = allInq[i]
          docsMap[String(r.leadId)] = { sitePhotos: 0, measurements: 0, docsOk: false }
        }
        this.docsMap = docsMap

        // C) ready for quotation -> readyForQuotation + leads
        const ready = Array.isArray(data && data.readyForQuotation) ? data.readyForQuotation : []
        this.readyForQuotation = ready.map((r) => {
          upsertLead(r.leadId, {
            id: r.leadId,
            name: r.customerName,
            phone: r.customerPhone,
            status: 'converted_to_inquiry',
            leadCode: r.leadCode,
            convertedAt: r.createdAt
          })
          // docs ok for these
          this.docsMap[String(r.leadId)] = { sitePhotos: 0, measurements: 0, docsOk: true }
          return {
            id: r.leadId,
            name: r.customerName,
            phone: r.customerPhone,
            leadCode: r.leadCode,
            convertedAt: r.docsCompletedAt || r.createdAt
          }
        })

        // D) quote lanes -> quotes list for existing kanban
        const qa = (data && data.quoteActionNeeded) ? data.quoteActionNeeded : {}
        const needSend = Array.isArray(qa.needSend) ? qa.needSend : []
        const waitingSig = Array.isArray(qa.waitingSignature) ? qa.waitingSignature : []
        // Optional: show “needQuotePdf” in drafts lane too (it’s a real action item)
        const needPdf = Array.isArray(qa.needQuotePdf) ? qa.needQuotePdf : []

        const quotes = []
        needPdf.forEach((r) => quotes.push(this.normalizeQuoteActionRow(r, 'draft')))
        needSend.forEach((r) => quotes.push(this.normalizeQuoteActionRow(r, r.status || 'draft')))
        waitingSig.forEach((r) => quotes.push(this.normalizeQuoteActionRow(r, 'sent')))

        this.quotes = quotes

        // finalize leads array
        const leads = Object.keys(leadsIndex).map(k => this.normalizeLead(leadsIndex[k]))
        this.leads = leads
      } catch(e){
        console.error('Dashboard load failed', e)
      } finally {
        this.loading = false
      }
    },

    async markDone(f){
      // No followup id anymore; follow-up is on lead row
      if (!f || !f.leadId) return
      try{
        // Common patch shape (adjust if your backend expects snake_case)
        await this.$api.patch(`/leads/${f.leadId}`, { followUpDate: null })
        await this.loadAll()
      } catch(e){
        console.error('markDone failed', e)
        // Fallback: at least take user to lead detail to update manually
        this.$router.push(this.toLead(f.leadId))
      }
    },

    shortId(id){
      const s = String(id || '')
      return s.length > 6 ? s.slice(-6) : s
    },

    async createDraftForInquiry(leadId){
      try{
        const inquiryId = this.inquiryIdForLead(leadId)
        if (!inquiryId) {
          this.$router.push(this.toLead(leadId))
          return
        }

        const res = await this.$api.post('/quotations', {
          inquiryId: String(inquiryId),
          revisionNote: null
        })

        // After creation, go to quotations screen for this inquiry
        this.$router.push(this.toQuote(inquiryId, leadId))
        await this.loadAll()
      } catch(e){
        console.error('createDraftForInquiry failed', e)
        this.$router.push(this.toLead(leadId))
      }
    }

  }
}
</script>

<style scoped>
/* Layout */
.dashboard{ padding-bottom: 90px; } /* room for floating dock */

/* HERO */
.hero{
  position:relative;
  border-radius:18px;
  overflow:hidden;
  border:1px solid #e5e7eb;
  background-size: cover;
  background-position: center;
  min-height: 240px;
  box-shadow: 0 12px 30px rgba(16,24,40,.08);
}
.hero__overlay{
  position:absolute; inset:0;
  background:
    radial-gradient(1200px 380px at 10% 10%, rgba(255,255,255,.75) 0%, rgba(255,255,255,.55) 45%, rgba(255,255,255,.25) 70%, rgba(255,255,255,.10) 100%),
    linear-gradient(90deg, rgba(255,255,255,.95) 0%, rgba(255,255,255,.80) 48%, rgba(255,255,255,.40) 100%);
}
.hero__content{
  position:relative;
  z-index:1;
  display:grid;
  grid-template-columns: 1.2fr .8fr;
  gap:12px;
  padding:18px;
}
@media (max-width: 980px){
  .hero__content{ grid-template-columns: 1fr; }
}
.hero__kicker{
  font-size:12px;
  color:#6b7280;
  letter-spacing:.2px;
  text-transform: uppercase;
}
.hero__title{
  margin:6px 0 0 0;
  font-size:26px;
  letter-spacing:.2px;
}
.hero__sub{
  margin:4px 0 0 0;
  color:#6b7280;
  font-size:13px;
}
.hero__summary{
  display:flex; flex-wrap:wrap; gap:8px;
  margin-top:12px;
}
.hero__pill{
  display:flex; align-items:center; gap:8px;
  padding:8px 10px;
  border-radius:999px;
  border:1px solid rgba(229,231,235,.9);
  background: rgba(255,255,255,.7);
}
.hero__pill .label{ color:#374151; font-size:12px; }
.hero__pill .value{ font-weight:800; color:#111827; font-size:12px; }
.dot{ width:8px; height:8px; border-radius:999px; display:inline-block; }
.dot--red{ background:#dc2626 }
.dot--amber{ background:#92400e }
.dot--orange{ background:#b45309 }
.dot--blue{ background:#2563eb }

.hero__actions{
  display:flex;
  gap:10px;
  align-items:center;
  margin-top:12px;
  flex-wrap:wrap;
}
.hero__hint{
  font-size:12px;
  color:#6b7280;
}

/* Spotlight */
.card--glass{
  background: rgba(255,255,255,.65);
  border:1px solid rgba(229,231,235,.9);
  box-shadow: 0 10px 25px rgba(16,24,40,.06);
}
.spotlight__item{
  display:flex;
  justify-content:space-between;
  gap:10px;
  padding:10px 0;
  border-bottom: 1px solid rgba(229,231,235,.7);
}
.spotlight__item:last-child{ border-bottom:none }
.spotlight__cta{ display:flex; align-items:flex-start; }

/* Priority chip bar */
.chipbar{
  display:flex;
  gap:10px;
  flex-wrap:wrap;
  margin-top:12px;
}
.chip{
  display:flex;
  align-items:center;
  gap:10px;
  padding:10px 12px;
  border-radius:999px;
  border:1px solid #e5e7eb;
  background:#fff;
  font-weight:700;
  font-size:13px;
  cursor:pointer;
  transition: transform .15s ease, box-shadow .15s ease;
}
.chip:hover{
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(16,24,40,.06);
}
.chip__count{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  min-width:26px;
  padding:4px 8px;
  border-radius:999px;
  font-size:12px;
  border:1px solid #e5e7eb;
  background:#f3f4f6;
}
.chip--quiet{ opacity:.55 }
.chip--hot{ box-shadow: 0 10px 22px rgba(220,38,38,.10) }
.chip--red.chip--hot{ border-color:#fecaca }
.chip--amber.chip--hot{ border-color:#fed7aa }
.chip--orange.chip--hot{ border-color:#fed7aa }
.chip--blue.chip--hot{ border-color:#bfdbfe }

/* Pipeline */
.pipeline{ margin-top:14px; }
.pipeline__header{
  display:flex;
  justify-content:space-between;
  gap:12px;
  align-items:flex-start;
  margin-bottom:12px;
}
.pipeline__title{ font-weight:800; font-size:14px; }
.pipeline__sub{ color:#6b7280; font-size:12px; margin-top:2px; }
.pipeline__meta{ color:#6b7280; font-size:12px; }

.pipeline__stages{
  display:grid;
  grid-template-columns: repeat(7, 1fr);
  gap:10px;
}
@media (max-width: 980px){
  .pipeline__stages{ grid-template-columns: repeat(2, 1fr); }
}
.stage{
  padding:10px;
  border:1px solid #eef2f7;
  border-radius:12px;
  background: #fff;
}
.stage__top{ display:flex; justify-content:space-between; gap:10px; align-items:center; }
.stage__name{ font-size:12px; color:#6b7280; font-weight:700; }
.stage__count{ font-size:14px; font-weight:900; color:#111827; }
.stage__bar{
  height:10px;
  border-radius:999px;
  background:#f3f4f6;
  border:1px solid #e5e7eb;
  overflow:hidden;
  margin-top:8px;
}
.stage__fill{
  height:100%;
  background: linear-gradient(90deg, rgba(239,68,68,.85), rgba(239,68,68,.55));
  width:0%;
}
.stage__bar--warn .stage__fill{
  background: linear-gradient(90deg, rgba(180,83,9,.85), rgba(180,83,9,.55));
}
.stage__bar--amber .stage__fill{
  background: linear-gradient(90deg, rgba(146,64,14,.85), rgba(146,64,14,.55));
}
.stage__bar--blue .stage__fill{
  background: linear-gradient(90deg, rgba(37,99,235,.85), rgba(37,99,235,.55));
}
.stage__bar--red .stage__fill{
  background: linear-gradient(90deg, rgba(220,38,38,.85), rgba(220,38,38,.55));
}

/* ===================== */
/* Masonry layout         */
/* ===================== */
.masonry{
  column-count: 2;
  column-gap: 12px;
}
@media (max-width: 980px){
  .masonry{ column-count: 1; }
}
.card--masonry{
  display:inline-block;
  width:100%;
  margin: 0 0 12px;
  break-inside: avoid;
}

/* Cards / items */
.card{
  border:1px solid #e5e7eb;
  border-radius:14px;
  background:#fff;
  padding:14px;
  box-shadow: 0 1px 2px rgba(16,24,40,.04);
  transition: box-shadow .25s ease, transform .25s ease, border-color .25s ease;
  scroll-margin-top: 96px;
}
.card:hover{
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(16,24,40,.06);
}
.row--header{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  padding-bottom:10px;
  border-bottom:1px solid #f0f0f0;
  margin-bottom:6px;
}
.item{
  display:flex;
  gap:10px;
  padding:10px 0;
  border-bottom:1px solid #f3f4f6;
}
.item:last-child{border-bottom:none}
.item__main{flex:1;min-width:0}
.item__title{font-weight:800;text-decoration:none;color:#111827}
.item__title:hover{text-decoration:underline}
.item__side{display:flex;align-items:flex-start; gap:8px;}
.item--queue{ border-radius:12px; }
.muted{ color:#6b7280; font-size:13px; }
.helper{ color:#6b7280; font-size:13px; }

/* Cap height + internal scroll */
.card__body--cap{
  max-height: 380px;
  overflow: auto;
  padding-right: 6px;
}
.card__body--cap::-webkit-scrollbar{ width: 8px; }
.card__body--cap::-webkit-scrollbar-thumb{ background: #e5e7eb; border-radius: 999px; }
.card__body--cap::-webkit-scrollbar-track{ background: transparent; }

.card__footer{
  border-top: 1px solid #f3f4f6;
  padding-top: 10px;
  margin-top: 8px;
  display:flex;
  justify-content:flex-end;
}
.footerLink{
  background: transparent;
  border:0;
  padding:0;
  color:#111827;
  font-weight:900;
  font-size:12px;
  cursor:pointer;
  text-decoration:none;
}
.footerLink:hover{ text-decoration: underline; }

.badge{
  padding:2px 8px;
  border-radius:999px;
  font-size:12px;
  border:1px solid #e5e7eb;
  background:#f3f4f6;
}
.badge--green{ background:#14532d;color:#dcfce7;border-color:#14532d }
.badge--red{ background:#7f1d1d;color:#fee2e2;border-color:#7f1d1d }
.badge--amber{ background:#92400e;color:#fff7ed;border-color:#92400e }
.empty{padding:12px;color:#6b7280}
.empty--tight{ padding: 10px 4px; }

/* Buttons */
.btn{
  background:#ef4444;
  color:#fff;
  border:0;
  padding:10px 14px;
  border-radius:10px;
  cursor:pointer;
  font-weight:800;
}
.btn.secondary{ background:#fff; color:#111827; border:1px solid #e5e7eb }
.btn.small{ padding:8px 12px; border-radius:10px; font-weight:800; font-size:12px; }
.btn.tiny{ padding:7px 10px; border-radius:10px; font-weight:800; font-size:12px; }

/* Segmented control */
.seg{
  display:flex;
  border:1px solid #e5e7eb;
  background:#fff;
  border-radius:999px;
  overflow:hidden;
}
.seg__btn{
  padding:7px 10px;
  font-size:12px;
  border:0;
  background:transparent;
  cursor:pointer;
  color:#374151;
  font-weight:800;
}
.seg__btn.active{
  background:#111827;
  color:#fff;
}

/* ===================== */
/* Kanban strip           */
/* ===================== */
.kanban{
  margin-top: 14px;
  border:1px solid #e5e7eb;
  border-radius:16px;
  background: linear-gradient(180deg, rgba(249,250,251,1), rgba(255,255,255,1));
  box-shadow: 0 10px 25px rgba(16,24,40,.06);
  padding: 14px;
  scroll-margin-top: 96px;
}
.kanban__top{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eef2f7;
  margin-bottom: 12px;
}
.kanban__title{ font-weight:900; font-size:14px; color:#111827; }
.kanban__sub{ font-size:12px; color:#6b7280; margin-top:2px; }
.kanban__actions{ display:flex; gap:8px; flex-wrap:wrap; }

.kanban__strip{
  display:flex;
  gap:12px;
  overflow:auto;
  padding-bottom: 4px;
}
.kanban__strip::-webkit-scrollbar{ height: 10px; }
.kanban__strip::-webkit-scrollbar-thumb{ background:#e5e7eb; border-radius:999px; }
.kanban__strip::-webkit-scrollbar-track{ background:transparent; }

.lane{
  min-width: 360px;
  max-width: 420px;
  flex: 0 0 auto;
  border:1px solid #eef2f7;
  border-radius:14px;
  background:#fff;
  box-shadow: 0 1px 2px rgba(16,24,40,.04);
  display:flex;
  flex-direction:column;
}
@media (max-width: 980px){
  .lane{ min-width: 300px; }
}
.lane__head{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:10px;
  padding:12px 12px 10px 12px;
  border-bottom:1px solid #f3f4f6;
}
.lane__label{
  font-weight:900;
  font-size:13px;
  color:#111827;
  display:flex;
  align-items:center;
  gap:8px;
}
.lane__dot{
  width:8px;
  height:8px;
  border-radius:999px;
  display:inline-block;
}
.lane__dot--amber{ background:#92400e; }
.lane__dot--blue{ background:#2563eb; }
.lane__dot--red{ background:#dc2626; }

.lane__body{
  padding: 10px 12px;
  max-height: 360px;
  overflow:auto;
}
.lane__body::-webkit-scrollbar{ width: 8px; }
.lane__body::-webkit-scrollbar-thumb{ background:#e5e7eb; border-radius:999px; }
.lane__body::-webkit-scrollbar-track{ background:transparent; }

.lane__foot{
  padding: 10px 12px 12px 12px;
  border-top: 1px solid #f3f4f6;
  display:flex;
  justify-content:flex-end;
}

.laneCard{
  border:1px solid #eef2f7;
  border-radius:12px;
  background:#fff;
  padding:10px;
  display:flex;
  justify-content:space-between;
  gap:10px;
  margin-bottom:10px;
  box-shadow: 0 1px 2px rgba(16,24,40,.03);
}
.laneCard:last-child{ margin-bottom:0; }
.laneCard--danger{
  border-color: rgba(220,38,38,.18);
  background: linear-gradient(180deg, rgba(254,242,242,1), rgba(255,255,255,1));
}
.laneCard__main{ flex:1; min-width:0; }
.laneCard__title{
  font-weight:900;
  text-decoration:none;
  color:#111827;
}
.laneCard__title:hover{ text-decoration: underline; }
.laneCard__cta{ display:flex; align-items:flex-start; }

/* Floating dock */
.dock{
  position: fixed;
  right: 18px;
  bottom: 18px;
  display:flex;
  gap:10px;
  align-items:center;
  padding:10px;
  border-radius:16px;
  background: rgba(255,255,255,.9);
  border:1px solid #e5e7eb;
  box-shadow: 0 16px 30px rgba(16,24,40,.14);
  backdrop-filter: blur(10px);
  z-index: 50;
}
.dock__btn{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  padding:10px 12px;
  border-radius:12px;
  border:1px solid #e5e7eb;
  background:#fff;
  color:#111827;
  text-decoration:none;
  font-weight:900;
  font-size:12px;
  cursor:pointer;
}
.dock__btn--primary{
  background:#111827;
  border-color:#111827;
  color:#fff;
}
.dock__btn:disabled{ opacity:.6; cursor:not-allowed; }

/* Flash highlight */
.card--flash{
  border-color: rgba(239,68,68,.55) !important;
  box-shadow:
    0 0 0 4px rgba(239,68,68,.16),
    0 18px 40px rgba(239,68,68,.10) !important;
  transform: translateY(-1px);
  animation: flashPulse 2.4s ease-in-out;
}
@keyframes flashPulse{
  0%   { box-shadow: 0 0 0 0 rgba(239,68,68,.0), 0 0 0 rgba(0,0,0,0); }
  25%  { box-shadow: 0 0 0 6px rgba(239,68,68,.18), 0 20px 45px rgba(239,68,68,.10); }
  55%  { box-shadow: 0 0 0 4px rgba(239,68,68,.16), 0 18px 40px rgba(239,68,68,.10); }
  100% { box-shadow: 0 0 0 0 rgba(239,68,68,.0), 0 0 0 rgba(0,0,0,0); }
}
</style>
